import { IPoint, distance } from "azure-devops-ui/Utilities/Position";
import React, { RefObject } from "react";
import { useEventListener } from "./uselistener";
import { useMouseCapture } from "./usemousecapture";
import { useTouchCapture } from "./usetouchcapture";

export interface IZoomOptions {
  imageReference: RefObject<HTMLDivElement>;
  containerReference: RefObject<HTMLDivElement>;
  onEventCallback: (xPosition: number, yPosition: number, scale: number) => void;
  limitPadding?: number;
}

interface IZoomStatus {
  containerBounds: DOMRect | undefined;
  lastPoints: IPoint[];
  lastAveragePoint: IPoint | undefined;
  scale: number;
  xPosition: number;
  yPosition: number;
}

export interface IZoomEvents {
  onMouseDown: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onTouchStart: (event: React.TouchEvent<HTMLDivElement>) => void;
  onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
}

/**
 * Provides events for zoom and pan
 * @param zoomOptions
 * @returns onWheel for mouse wheel events and onMouseDown and onTouchStart for touch and mouse events
 */
export function usePhotoZoom(zoomOptions: IZoomOptions): IZoomEvents {
  const { limitPadding = 50, onEventCallback } = zoomOptions;

  // number of active touch/mouse presses
  const zoomStatus = React.useRef<IZoomStatus>({
    containerBounds: undefined,
    lastPoints: [],
    lastAveragePoint: undefined,
    scale: 1,
    xPosition: 0,
    yPosition: 0
  });

  const { onMouseDown: _onMouseDown } = useMouseCapture(pointerMoved);
  const { onTouchStart: _onTouchStart } = useTouchCapture(touchMoved);

  updateContainerBounds();
  useEventListener(window, "resize", updateContainerBounds);

  return { onWheel, onMouseDown, onTouchStart };

  function updateContainerBounds() {
    const containerRect = zoomOptions.imageReference.current?.getBoundingClientRect();

    if (
      !zoomStatus.current.containerBounds ||
      (containerRect &&
        (containerRect.height < zoomStatus.current.containerBounds?.height || containerRect?.width < zoomStatus.current.containerBounds?.width))
    ) {
      zoomStatus.current.containerBounds = containerRect;
      updateValues(zoomStatus.current.xPosition, zoomStatus.current.yPosition, zoomStatus.current.scale);
    }
  }

  /**
   * Updates the scale and xy values
   * @param newDx New x cordinate
   * @param newDy New y coordinate
   * @param newScale New zoom scale
   * @param targetBounds Element reference to the content
   */
  function updateValues(newDx: number, newDy: number, newScale: number, targetBounds?: DOMRect) {
    if (zoomOptions.containerReference.current !== zoomOptions.imageReference.current && zoomStatus.current.containerBounds) {
      targetBounds = targetBounds || zoomOptions.containerReference.current!.getBoundingClientRect();

      if (newDx !== zoomStatus.current.xPosition || newDy !== zoomStatus.current.yPosition || newScale !== zoomStatus.current.scale) {
        const scaleDelta = 1 / (zoomStatus.current.scale / newScale);

        // make sure content doesnt go out of bound
        newDy = Math.min(0, Math.max(newDy, zoomStatus.current.containerBounds.height - targetBounds.height * scaleDelta - limitPadding));
        newDx = Math.min(0, Math.max(newDx, zoomStatus.current.containerBounds.width - targetBounds.width * scaleDelta - limitPadding));
      }
    }

    zoomStatus.current.scale = Math.max(1, newScale);
    zoomStatus.current.xPosition = newDx;
    zoomStatus.current.yPosition = newDy;
  }

  /**
   * Function to change scale depending on the wheel direction. Binds onWheel
   * event to zoom in or out and calls onEventCallback with the updated dx dy
   * and scale values
   * @param event onWheel mouse event
   * @returns
   */
  function onWheel(event: React.WheelEvent<HTMLDivElement>) {
    updateContainerBounds();
    const targetBounds = zoomOptions.containerReference.current?.getBoundingClientRect();

    if (!targetBounds) {
      return;
    }

    // wheel down should zoom in instead of wheel up
    let delta = -1 * event.deltaY;

    if (event.deltaMode === 1) {
      // 1 is "lines", 0 is "pixels"
      // Firefox uses "lines" when mouse is connected
      delta *= 15;
    }

    // stop mouse wheel producing huge values
    delta = Math.max(Math.min(delta, 60), -60);
    const scaleDiff = delta / 300 + 1;

    // max zoom in limit
    if (zoomStatus.current.scale * scaleDiff > 10) {
      return;
    }

    updateValues(
      zoomStatus.current.xPosition - (event.pageX - targetBounds.left) * (scaleDiff - 1),
      zoomStatus.current.yPosition - (event.pageY - targetBounds.top) * (scaleDiff - 1),
      zoomStatus.current.scale * scaleDiff,
      targetBounds
    );

    onEventCallback(zoomStatus.current.xPosition, zoomStatus.current.yPosition, zoomStatus.current.scale);
  }

  /**
   * Function to pan a zoomed image. Binds mouse move and touch move
   * events to update dx and dy coordinates depending on the
   * move direction and call onEventCallback
   * @param event MouseDown or touch event
   * @returns
   */
  function onMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (event.type === "mousedown" && event.button !== 0) {
      return;
    }

    zoomStatus.current.lastPoints = [
      {
        x: event.pageX,
        y: event.pageY
      }
    ];
    zoomStatus.current.lastAveragePoint = zoomStatus.current.lastPoints.reduce(getMidpoint);

    _onMouseDown(event);
  }

  function onTouchStart(event: React.TouchEvent<HTMLDivElement>) {
    zoomStatus.current.lastPoints = Array.from(event.touches).map((event: Touch) => {
      return {
        x: event.pageX,
        y: event.pageY
      };
    });
    zoomStatus.current.lastAveragePoint = zoomStatus.current.lastPoints.reduce(getMidpoint);

    _onTouchStart(event);
  }

  function pointerMoved(event: MouseEvent) {
    // its only pan-able when its zoomed in
    if (zoomStatus.current.scale <= 1) {
      return;
    }

    // when zoomed in we dont wnat the image to be dragged when panning
    event.preventDefault();

    const points = [
      {
        x: event.pageX,
        y: event.pageY
      }
    ];
    const averagePoint = points.reduce(getMidpoint);

    // istanbul ignore else - must have lastAveragePoint if the image is zoomed in
    if (zoomStatus.current.lastAveragePoint) {
      const tmpX = zoomStatus.current.xPosition + averagePoint.x - zoomStatus.current.lastAveragePoint.x;
      const tmpY = zoomStatus.current.yPosition + averagePoint.y - zoomStatus.current.lastAveragePoint.y;

      updateValues(tmpX, tmpY, zoomStatus.current.scale);
    }

    zoomOptions.onEventCallback(zoomStatus.current.xPosition, zoomStatus.current.yPosition, zoomStatus.current.scale);
    zoomStatus.current.lastPoints = points;
    zoomStatus.current.lastAveragePoint = averagePoint;
  }

  function touchMoved(event: TouchEvent) {
    // its only pan-able when its zoomed in
    if (zoomStatus.current.scale === 1) {
      return;
    }

    const points = Array.from(event.touches).map((event: Touch) => {
      return {
        x: event.pageX,
        y: event.pageY
      };
    });

    const averagePoint = points.reduce(getMidpoint);
    const targetBounds = zoomOptions.containerReference.current!.getBoundingClientRect();

    // istanbul ignore else - must have lastAveragePoint if the image is zoomed in
    if (zoomStatus.current.lastAveragePoint) {
      const tmpX = zoomStatus.current.xPosition + averagePoint.x - zoomStatus.current.lastAveragePoint.x;
      const tmpY = zoomStatus.current.yPosition + averagePoint.y - zoomStatus.current.lastAveragePoint.y;

      // check if its a pinch zoom
      if (points.length > 1) {
        const scaleDiff = distance(points[0], points[1]) / distance(zoomStatus.current.lastPoints[0], zoomStatus.current.lastPoints[1]);

        updateValues(
          tmpX - (averagePoint.x - targetBounds.left) * (scaleDiff - 1),
          tmpY - (averagePoint.y - targetBounds.top) * (scaleDiff - 1),
          zoomStatus.current.scale * scaleDiff,
          targetBounds
        );
      } else {
        updateValues(tmpX, tmpY, zoomStatus.current.scale);
      }
    }

    zoomOptions.onEventCallback(zoomStatus.current.xPosition, zoomStatus.current.yPosition, zoomStatus.current.scale);
    zoomStatus.current.lastPoints = points;
    zoomStatus.current.lastAveragePoint = averagePoint;
  }

  function getMidpoint(point1: IPoint, point2: IPoint): IPoint {
    return {
      x: (point1.x + point2.x) / 2,
      y: (point1.y + point2.y) / 2
    };
  }
}
